import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["track", "item"]
  static values = {
    interval: { type: Number, default: 6500 },      // ms between slides
    resumeAfter: { type: Number, default: 3500 }    // ms after user interaction to resume
  }

  connect() {
    // If only 0/1 item, do nothing
    if (!this.hasTrackTarget || this.itemTargets.length <= 1) return

    this.currentIndex = this.nearestIndex()
    this.isPaused = false

    this.start()
  }

  disconnect() {
    this.stop()
    this.clearResumeTimer()
  }

  // ---------- Public actions ----------

  pause() {
    this.isPaused = true
    this.stop()
    this.clearResumeTimer()
  }

  scheduleResume() {
    this.clearResumeTimer()
    this.resumeTimer = setTimeout(() => {
      this.isPaused = false
      this.start()
    }, this.resumeAfterValue)
  }

  onScroll() {
    // Update currentIndex while user scrolls; keep it lightweight.
    // If you want even less work, you can throttle, but this is usually fine.
    this.currentIndex = this.nearestIndex()
  }

  // ---------- Internal ----------

  start() {
    if (this.timer || this.isPaused) return
    this.timer = setInterval(() => this.next(), this.intervalValue)
  }

  stop() {
    if (!this.timer) return
    clearInterval(this.timer)
    this.timer = null
  }

  clearResumeTimer() {
    if (!this.resumeTimer) return
    clearTimeout(this.resumeTimer)
    this.resumeTimer = null
  }

  next() {
    const count = this.itemTargets.length
    if (count <= 1) return

    const nextIndex = (this.currentIndex + 1) % count
    this.scrollToIndex(nextIndex)
    this.currentIndex = nextIndex
  }

  scrollToIndex(index) {
    const el = this.itemTargets[index]
    if (!el) return

    // Scroll the carousel so this item aligns to the left (snap-start)
    this.trackTarget.scrollTo({
      left: el.offsetLeft - this.trackTarget.offsetLeft,
      behavior: "smooth"
    })
  }

  nearestIndex() {
    // Find item whose left edge is closest to track's scrollLeft
    const left = this.trackTarget.scrollLeft
    let bestIdx = 0
    let bestDist = Infinity

    this.itemTargets.forEach((el, idx) => {
      const x = el.offsetLeft - this.trackTarget.offsetLeft
      const dist = Math.abs(x - left)
      if (dist < bestDist) {
        bestDist = dist
        bestIdx = idx
      }
    })

    return bestIdx
  }
}
